/** Tracked tasks as the task page and the agent read and edit them. See CONTEXT.md and ADR-0060. */

/** An ordinary tracked task or a development ticket (ADR-0073); never inferred from a title. */
export const TASK_KINDS = ["ordinary", "development"] as const;
export type TaskKind = (typeof TASK_KINDS)[number];

/** An ordinary task's owner-managed states. */
export const TASK_STATUSES = ["todo", "in_progress", "done", "closed"] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

/** A development ticket's lifecycle, established by its spec, execution and deployment, never set by hand. */
export const DEVELOPMENT_STATUSES = ["undefined", "ready", "in_progress", "ready_to_deploy", "completed"] as const;
export type DevelopmentStatus = (typeof DEVELOPMENT_STATUSES)[number];

/** A tracked task's state, of either kind. */
export type TrackedStatus = TaskStatus | DevelopmentStatus;
export const TRACKED_STATUSES: readonly TrackedStatus[] = [...new Set<TrackedStatus>([...TASK_STATUSES, ...DEVELOPMENT_STATUSES])];

export const TASK_PRIORITIES = ["low", "medium", "high"] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

/** The ordinary states the list shows when no state filter is chosen. */
export const ACTIVE_TASK_STATUSES: readonly TaskStatus[] = ["todo", "in_progress"];

/** The development states the list shows when no state filter is chosen. */
export const ACTIVE_DEVELOPMENT_STATUSES: readonly DevelopmentStatus[] = ["undefined", "ready", "in_progress", "ready_to_deploy"];

/** Whether a task is still being followed: an unarchived open ordinary task or an undeployed development ticket. */
export function isActiveTask(task: Pick<TaskRecord, "kind" | "status" | "archived">): boolean {
	if (task.archived) return false;
	return task.kind === "development"
		? (ACTIVE_DEVELOPMENT_STATUSES as readonly string[]).includes(task.status)
		: (ACTIVE_TASK_STATUSES as readonly string[]).includes(task.status);
}

/** The agent's tool that reads one task by its exact id. The chat offers `@` task mentions only where a session has it. */
export const GET_TASK = "get_task";

export interface TaskRecord {
	id: string;
	owner: string;
	title: string;
	description: string;
	kind: TaskKind;
	/** A TaskStatus for an ordinary task, a DevelopmentStatus for a development ticket. */
	status: TrackedStatus;
	/** Taken out of active tracking and pickup by its owner; separate from the status and never completion. */
	archived: boolean;
	priority: TaskPriority;
	/** A calendar date, YYYY-MM-DD. */
	dueDate: string | null;
	projectId: string | null;
	/** The linked project's title when linked, kept after the project is archived or removed. */
	projectTitle: string | null;
	/** The dev change a development ticket's prepared spec bound it to, by slug (ADR-0073); null when none. */
	devChange: string | null;
	createdAt: number;
	updatedAt: number;
}

/** A development ticket's spec as dev prepared it: the exact version its owner confirms. */
/** The profile whose job asked dev for a development ticket, and that job's session (ADR-0084). */
export interface TicketRequest {
	profile: string;
	sessionId: string;
}

export interface TicketSpec {
	version: string;
	/** spec.md as it was imported from the dev workspace. */
	content: string;
	/** Whether the owner confirmed this exact version, and it still holds. */
	confirmed: boolean;
	change: string;
	/** The spec, check or requirements changed since it was prepared: dev prepares it again. */
	stale: boolean;
	preparedAt: number;
	confirmedAt: number | null;
}

/** One file of a captured acceptance check, by its path under the change's scratch directory. */
export interface AcceptanceBundleFile {
	path: string;
	/** utf8 for text that round-trips byte for byte, else base64 of the exact bytes. */
	encoding: "utf8" | "base64";
	content: string;
	executable: boolean;
}

/** A change's check as captured: accept.sh and its optional tests/, with their checkDigest. */
export interface AcceptanceBundle {
	checkDigest: string;
	files: AcceptanceBundleFile[];
}

/** Where an acceptance revision stands (CONTEXT.md "Acceptance revision"); stale and superseded are final. */
export type AcceptanceRevisionState = "prepared" | "attested" | "approved" | "superseded" | "stale";

/** One run of a candidate check in a disposable checkout. */
export interface AcceptanceRevisionRun {
	sha: string;
	exitCode: number | null;
	/** passed: exit 0; broken: no exit, or 126 and up; failed: any other exit. */
	outcome: "passed" | "failed" | "broken";
	output: string;
}

/** Dev's run of a revision's candidate on pushed main and on the stopped branch's committed code. */
export interface AcceptanceRevisionReview {
	reviewId: string;
	main: AcceptanceRevisionRun;
	/** The branch's committed HEAD; `uncommitted` names worktree changes the run did not include. */
	worktree: AcceptanceRevisionRun & { uncommitted: string[] };
	specDigest: string;
	checkDigest: string;
	reviewedBy: string;
	reviewedAt: number;
}

/** Dev's attestation, from one review, that the candidate repairs the harness without weakening acceptance. */
export interface AcceptanceRevisionAttestation {
	reviewId: string;
	reason: string;
	standardUnchanged: true;
	mainVerdict: "target_missing";
	worktreeVerdict: "passed" | "target_missing";
	attestedBy: string;
	attestedAt: number;
}

/** The owner's explicit approval of one exact revision. */
export interface AcceptanceRevisionApproval {
	version: string;
	owner: string;
	confirmedAt: number;
}

/** An owner-approved repair of a picked ticket's defective check, or a proposal of one (ADR-0077). */
export interface AcceptanceRevision {
	taskId: string;
	version: string;
	/** The active revision it replaces; null when it replaces the originally confirmed check. */
	parentVersion: string | null;
	state: AcceptanceRevisionState;
	/** Why it went stale or was superseded; null otherwise. */
	stateReason: string | null;
	/** The confirmed spec version it leaves unchanged, and that spec's digest. */
	specVersion: string;
	specDigest: string;
	/** The stopped run it was prepared from, its change, branch and committed HEAD then. */
	loopId: string;
	change: string;
	branch: string;
	worktreeSha: string;
	/** Dev's explanation of the harness defect. */
	reason: string;
	before: AcceptanceBundle;
	after: AcceptanceBundle;
	diff: string;
	preparedBy: string;
	preparedAt: number;
	/** Oldest first. */
	reviews: AcceptanceRevisionReview[];
	/** The latest attestation, or null; every one is in attestations, oldest first. */
	attestation: AcceptanceRevisionAttestation | null;
	attestations: AcceptanceRevisionAttestation[];
	approval: AcceptanceRevisionApproval | null;
}

/** A ticket's acceptance revisions, oldest first, and the approved one in force. */
export interface AcceptanceRevisionHistory {
	revisions: AcceptanceRevision[];
	activeVersion: string | null;
}

/** One ready change of the shared deployment batch, as anyone may see it: no ticket's private content. */
export interface DeploymentChange {
	id: string;
	change: string;
	title: string;
	pushedSha: string;
	pushedAt: number | null;
}

/** A commit pushed to main from elsewhere, not by the push lane, whose checks have passed. */
export interface DeploymentCommit {
	sha: string;
	subject: string;
	/** The ready change whose push carried it through the checks; null when check_out_of_lane checked it. */
	checkedBy: string | null;
}

/** GET /api/tasks/deployment: the shared ready batch the server deploys together. */
export interface DeploymentBatch {
	ready: DeploymentChange[];
	/** Checked commits pushed from elsewhere, which go live with the batch. */
	elsewhere: DeploymentCommit[];
	/** The commit an approval pins: every ready change up to it. */
	newestSha: string | null;
	/** Why an approval would be refused now; null when none is known. */
	blocked: string | null;
}

/** A real calendar date written YYYY-MM-DD. */
export function isCalendarDate(value: string): boolean {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
	const date = new Date(`${value}T00:00:00Z`);
	return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
